/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useRef} from 'react';
import {translate} from '@ion/core/src/locale/locale';
import {OfflineCanRequestResult} from '@ion/core/src/types/services';
import {CellMenu} from '@ion/dls/src/components/grid/CellMenu';
import {CellMenuItem} from '@ion/dls/src/components/grid/CellMenuItem';
import {CancelIcon} from '@ion/dls/src/icons/CancelIcon';
import {NotificationTimeIcon} from '@ion/dls/src/icons/NotificationTimeIcon';
import {RetryIcon} from '@ion/dls/src/icons/RetryIcon';
import {TerminalIcon} from '@ion/dls/src/icons/TerminalIcon';
import {ViewRecordIcon} from '@ion/dls/src/icons/ViewRecordIcon';
import Render from '@ion/dls/src/render/Render';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {canCancelCanRequest, canRetryCanRequest} from './CanRequestUtils';

export interface CanRequestRowEditorProps {
  request: OfflineCanRequestResult;
  onViewDetails?: (request: OfflineCanRequestResult) => void;
  onViewJson?: (request: OfflineCanRequestResult) => void;
  onRetry?: (request: OfflineCanRequestResult) => void;
  onCancel?: (request: OfflineCanRequestResult) => void;
  onAuditLog?: (request: OfflineCanRequestResult) => void;
  retrying?: boolean;
  cancelling?: boolean;
  canViewAuditLog?: boolean;
}

/**
 * Renders row-level actions for a CAN request within the requests grid.
 *
 * The menu exposes "View Details" for all rows, "Scout Audit Log" for users
 * permitted to view VEScout audit records, "Retry" for statuses supported
 * by the retry policy, and "Cancel Request" for statuses supported by the
 * cancel policy.
 *
 * ```typescript
 * import CanRequestRowEditor from
 *   '@ion/applications/src/pages/vehicle/manager/CanRequestRowEditor';
 * ...
 *
 * <CanRequestRowEditor request={request}
 *   onViewDetails={onViewDetails}
 *   onRetry={onRetry}
 *   onCancel={onCancel}
 *   retrying={retryingRequestId === request.requestId}
 *   cancelling={cancellingRequestId === request.requestId} />
 * ```
 *
 * @param   request The current row request payload.
 * @param   onViewDetails Invoked when the user selects view details.
 * @param   onRetry Invoked when the user selects retry.
 * @param   onCancel Invoked when the user selects cancel request.
 * @param   onAuditLog Invoked when the user selects Scout Audit Log.
 * @param   retrying Determines whether retry should show loading state.
 * @param   cancelling Determines whether cancel should show loading state.
 * @param   canViewAuditLog Determines whether Scout Audit Log is shown.
 * @returns The CAN request row action menu component.
 */
export const CanRequestRowEditor = ({
  request,
  onViewDetails,
  onViewJson,
  onRetry,
  onCancel,
  onAuditLog,
  retrying = false,
  cancelling = false,
  canViewAuditLog = false
}: CanRequestRowEditorProps) => {
  const cellMenuRef = useRef<any>(null);
  const canRetry    = canRetryCanRequest(request?.status);
  const canCancel   = canCancelCanRequest(request?.status);

  const closeMenu = useCallback(() => {
    cellMenuRef.current?.closeCellMenu?.(cellMenuRef.current, true);
  }, []);

  const onViewClick = useCallback(() => {
    closeMenu();
    onViewDetails?.(request);
  }, [closeMenu, onViewDetails, request]);

  const onRetryClick = useCallback(() => {
    closeMenu();
    onRetry?.(request);
  }, [closeMenu, onRetry, request]);

  const onCancelClick = useCallback(() => {
    closeMenu();
    onCancel?.(request);
  }, [closeMenu, onCancel, request]);

  const onViewJsonClick = useCallback(() => {
    closeMenu();
    onViewJson?.(request);
  }, [closeMenu, onViewJson, request]);

  const onAuditLogClick = useCallback(() => {
    closeMenu();
    onAuditLog?.(request);
  }, [closeMenu, onAuditLog, request]);

  return (
    <CellMenu ref={cellMenuRef}
      menuItems={
        <>
          <CellMenuItem icon={<ViewRecordIcon />}
            onClick={onViewClick}
            label={translate('vehicle-manager:vehicleHistoryGrid.canRequestsGrid.menu.viewDetails')} />
          <Render.If condition={canViewAuditLog}>
            <CellMenuItem icon={<NotificationTimeIcon color={dlsColor('grey3')} />}
              onClick={onAuditLogClick}
              label={translate('vehicle-manager:vehicleHistoryGrid.canRequestsGrid.menu.scoutAuditLog')} />
          </Render.If>
          <CellMenuItem icon={<TerminalIcon />}
            onClick={onViewJsonClick}
            label={translate('vehicle-manager:vehicleHistoryGrid.canRequestsGrid.menu.viewJson')} />
          <CellMenuItem icon={<RetryIcon />}
            onClick={onRetryClick}
            label={translate('vehicle-manager:vehicleHistoryGrid.canRequestsGrid.menu.retry')}
            disabled={!canRetry || retrying}
            isLoading={retrying} />
          <CellMenuItem icon={<CancelIcon />}
            onClick={onCancelClick}
            label={translate('vehicle-manager:vehicleHistoryGrid.canRequestsGrid.menu.cancel')}
            disabled={!canCancel || cancelling}
            isLoading={cancelling} />
        </>
      } />
  );
};

export default CanRequestRowEditor;
